import type { CurriculumCheckboxTreeProps } from '#src/components/CurriculumCheckboxTree/index.ts';
import { useQuery } from '@tanstack/react-query';
import { Duration } from 'luxon';
import { useCallback, useMemo } from 'react';
import { getReactQueryOptions } from '@_/utils-frontend';
import { publicApiQueryClient } from '#src/api.ts';
import {
  CurriculumCheckboxTree,
  expandCurriculumIds,
  reduceAcademicLevelIds
} from '#src/components/CurriculumCheckboxTree/index.ts';

function useFilterProps({
  onChange,
  value
}: {
  onChange: (value: { academicLevelIds?: string[]; curriculumIds?: string[] }) => void;
  value?: { academicLevelIds?: string[]; curriculumIds?: string[] };
}) {
  const curriculaQuery = useQuery({
    ...getReactQueryOptions(publicApiQueryClient.metadata.getCurricula),
    staleTime: Duration.fromObject({ minutes: 5 }).toMillis()
  });

  const curricula = useMemo(() => curriculaQuery.data?.data || [], [curriculaQuery.data]);

  return {
    curricula,
    loading: curriculaQuery.isPending,
    onChange: useCallback(
      academicLevelIds => {
        onChange?.(reduceAcademicLevelIds(curricula, academicLevelIds || []));
      },
      [curricula, onChange]
    ),
    value: useMemo(
      () =>
        expandCurriculumIds(curricula, value?.academicLevelIds || [], value?.curriculumIds || []),
      [curricula, value]
    )
  } satisfies CurriculumCheckboxTreeProps;
}

export function CurriculumFilter({
  disabled,
  ...rest
}: Pick<Parameters<typeof useFilterProps>[0], 'onChange' | 'value'> & {
  disabled?: boolean;
}) {
  const props = useFilterProps(rest);

  return <CurriculumCheckboxTree {...props} disabled={disabled} />;
}
